import * as React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../primitives/table';
import { Checkbox } from '../primitives/checkbox';
import { Button } from '../primitives/button';
import { Skeleton } from '../primitives/skeleton';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../primitives/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../primitives/collapsible';
import { cn } from '../lib/utils';

export type SortDirection = 'asc' | 'desc' | null;

export interface DataTableColumn<T> {
  id: string;
  header: React.ReactNode;
  accessor: keyof T | ((row: T) => unknown);
  cell?: (value: unknown, row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  selectable?: 'single' | 'multi';
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T) => string;
  paginated?: boolean;
  pageSize?: number;
  className?: string;
  /**
   * Per-row classes, for tables where the ROW itself carries meaning — a schema
   * diff tinting added / removed / changed fields, a variance grid flagging
   * out-of-tolerance lines.
   *
   * Without this a raw `<table>` doing that could not move to `DataTable` /
   * `ResponsiveTableShell` without silently dropping the signal, which is why 33
   * of the UX-A7 conversions were blocked. Returning `undefined` leaves the row
   * styled exactly as before.
   */
  rowClassName?: (row: T, index: number) => string | undefined;
}

function getAccessorValue<T>(
  row: T,
  accessor: keyof T | ((row: T) => unknown)
): unknown {
  if (typeof accessor === 'function') return accessor(row);
  return row[accessor];
}

const alignClass = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

/**
 * Fires a click-equivalent activation for a keyboard-operable row / card title.
 * Enter and Space both activate; Space additionally `preventDefault`s so the
 * page does not scroll. Returns `true` when it handled the key (the caller has
 * already run the action) so callers can skip further processing. Matches the
 * pointer-click contract exactly — the two input routes never diverge.
 */
export function activateOnEnterOrSpace(
  e: Pick<React.KeyboardEvent, 'key' | 'preventDefault'>,
  activate: () => void
): boolean {
  if (e.key === 'Enter' || e.key === ' ') {
    if (e.key === ' ') e.preventDefault();
    activate();
    return true;
  }
  return false;
}

/**
 * Props for a single {@link DataTable} body row. Flat + primitive so the
 * `React.memo` shallow comparison is correct: a row only re-renders when its
 * own data / selected state changes, not on every parent render (sort click,
 * pagination, selection of an unrelated row).
 */
interface DataTableRowProps<T> {
  row: T;
  index: number;
  id: string;
  columns: DataTableColumn<T>[];
  selectableMulti: boolean;
  isSelected: boolean;
  onRowClick?: (row: T, index: number) => void;
  onToggleRow?: (id: string) => void;
  /**
   * Already-resolved extra classes for this row. A RESOLVED STRING, not the
   * `rowClassName` function itself: callers write that function inline, so a new
   * identity every render would defeat the `React.memo` above — which the note
   * on this interface exists to protect.
   */
  extraClassName?: string;
}

function DataTableRowInner<T>({
  row,
  index,
  id,
  columns,
  selectableMulti,
  isSelected,
  onRowClick,
  onToggleRow,
  extraClassName,
}: DataTableRowProps<T>) {
  return (
    <TableRow
      data-state={isSelected ? 'selected' : undefined}
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      // Keyboard affordances only when the row is actually clickable — a plain
      // table stays a plain table with no spurious tabstops or button semantics.
      role={onRowClick ? 'button' : undefined}
      tabIndex={onRowClick ? 0 : undefined}
      aria-roledescription={
        onRowClick ? 'Row. Press Enter or Space to open.' : undefined
      }
      onKeyDown={
        onRowClick
          ? (e) => activateOnEnterOrSpace(e, () => onRowClick(row, index))
          : undefined
      }
      className={cn(
        onRowClick &&
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        extraClassName
      )}
    >
      {selectableMulti && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleRow ? () => onToggleRow(id) : undefined}
            aria-label="Select row"
          />
        </TableCell>
      )}
      {columns.map((col) => {
        const value = getAccessorValue(row, col.accessor);
        return (
          <TableCell key={col.id} className={cn(alignClass[col.align ?? 'left'])}>
            {col.cell
              ? col.cell(value, row)
              : value == null
              ? '—'
              : String(value)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

// `React.memo` cast preserves the generic signature (memo erases type params).
const DataTableRow = React.memo(DataTableRowInner) as typeof DataTableRowInner;

/**
 * Manufacturing-grade data table with sortable columns, selection, and
 * pagination. Composes the shadcn `Table` primitive.
 *
 * @example
 * ```tsx
 * const columns: DataTableColumn<WorkOrder>[] = [
 *   { id: 'number', header: 'WO #', accessor: 'orderNumber', sortable: true },
 *   { id: 'status', header: 'Status', accessor: 'status',
 *     cell: (v) => <StatusBadge status={String(v)} /> },
 * ];
 * <DataTable columns={columns} data={workOrders} paginated pageSize={20} />
 * ```
 */
export function DataTable<T>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data available',
  selectable,
  selectedIds = [],
  onSelectionChange,
  onRowClick,
  rowKey = (row) => String((row as { id?: string | number }).id ?? ''),
  paginated = false,
  pageSize = 10,
  className,
  rowClassName,
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);
  const [page, setPage] = React.useState(0);

  const handleSort = (columnId: string) => {
    if (sortColumn !== columnId) {
      setSortColumn(columnId);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') setSortDirection('desc');
    else if (sortDirection === 'desc') {
      setSortColumn(null);
      setSortDirection(null);
    } else setSortDirection('asc');
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn || !sortDirection) return data;
    const column = columns.find((c) => c.id === sortColumn);
    if (!column) return data;
    return [...data].sort((a, b) => {
      const av = getAccessorValue(a, column.accessor);
      const bv = getAccessorValue(b, column.accessor);
      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const a1 = av as number | string;
      const b1 = bv as number | string;
      const cmp = a1 < b1 ? -1 : a1 > b1 ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, sortColumn, sortDirection, columns]);

  const pageCount = paginated ? Math.ceil(sortedData.length / pageSize) : 1;

  // Clamp the page index when upstream filtering / sorting / pageSize changes
  // shrink the row count below the current window, so we never slice an
  // out-of-range (empty) page.
  React.useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
  }, [pageCount, page]);

  const pageData = React.useMemo(() => {
    if (!paginated) return sortedData;
    const safePage = Math.min(page, Math.max(0, pageCount - 1));
    const start = safePage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize, paginated, pageCount]);

  // Stable across renders so memoised rows keep their `onToggleRow` identity
  // (`selectedIds` is read fresh from the ref on each toggle, not closed over).
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const toggleRow = React.useCallback(
    (id: string) => {
      if (!onSelectionChange) return;
      const current = selectedIdsRef.current;
      if (selectable === 'single') {
        onSelectionChange(current.includes(id) ? [] : [id]);
      } else {
        onSelectionChange(
          current.includes(id)
            ? current.filter((s) => s !== id)
            : [...current, id]
        );
      }
    },
    [onSelectionChange, selectable]
  );

  const toggleAll = () => {
    if (!onSelectionChange) return;
    const ids = pageData.map(rowKey);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    onSelectionChange(
      allSelected
        ? selectedIds.filter((id) => !ids.includes(id))
        : Array.from(new Set([...selectedIds, ...ids]))
    );
  };

  const allSelected =
    pageData.length > 0 && pageData.every((r) => selectedIds.includes(rowKey(r)));

  return (
    <div className={cn('space-y-4', className)}>
      {/* overflow-x-auto: wide column sets scroll within the bordered frame on
          tablet / mobile instead of forcing the whole page to scroll sideways. */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable === 'multi' && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </TableHead>
              )}
              {columns.map((col) => {
                const ariaSort: React.AriaAttributes['aria-sort'] = col.sortable
                  ? sortColumn === col.id && sortDirection === 'asc'
                    ? 'ascending'
                    : sortColumn === col.id && sortDirection === 'desc'
                    ? 'descending'
                    : 'none'
                  : undefined;
                return (
                <TableHead
                  key={col.id}
                  aria-sort={ariaSort}
                  className={cn(col.width, alignClass[col.align ?? 'left'])}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.id)}
                      aria-label={`Sort by ${typeof col.header === 'string' ? col.header : col.id}, currently ${ariaSort ?? 'none'}`}
                      className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                    >
                      {col.header}
                      {sortColumn === col.id && sortDirection === 'asc' && (
                        <ChevronUp className="h-4 w-4" />
                      )}
                      {sortColumn === col.id && sortDirection === 'desc' && (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      {sortColumn !== col.id && (
                        <ChevronsUpDown className="h-4 w-4 opacity-30" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {selectable === 'multi' && (
                    <TableCell>
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable === 'multi' ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              pageData.map((row, index) => {
                const id = rowKey(row);
                const isSelected = selectedIds.includes(id);
                return (
                  <DataTableRow
                    key={id}
                    row={row}
                    index={index}
                    id={id}
                    columns={columns}
                    selectableMulti={selectable === 'multi'}
                    isSelected={isSelected}
                    onRowClick={onRowClick}
                    onToggleRow={onSelectionChange ? toggleRow : undefined}
                    extraClassName={rowClassName?.(row, index)}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {paginated && !loading && sortedData.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing{' '}
            <span className="font-medium">
              {page * pageSize + 1}–
              {Math.min((page + 1) * pageSize, sortedData.length)}
            </span>{' '}
            of <span className="font-medium">{sortedData.length}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
              disabled={page >= pageCount - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export interface ResponsiveTableShellProps<T> extends DataTableProps<T> {
  /**
   * Which column's value to use as the card title in the disclosure card-grid.
   * Defaults to the first column. Below the lg breakpoint the title column is
   * always shown collapsed; the remaining columns reveal on expand.
   */
  cardTitleColumnId?: string;
}

/**
 * Renders a {@link DataTable} at the `lg` breakpoint and above, and a sortable,
 * paginated disclosure card-grid below it. A dense manufacturing grid (many
 * columns) is unreadable on a phone / split-screen — horizontal scroll hides
 * exactly the columns an operator on the floor needs. The card-grid collapses
 * each row to a title + a few key fields and discloses the rest on tap, while
 * keeping the same sort + pagination semantics as the table so the two layouts
 * are interchangeable.
 *
 * Both layouts share the same `columns`/`data`, so a caller swaps `DataTable`
 * for `ResponsiveTableShell` with no other change.
 *
 * @example
 * ```tsx
 * <ResponsiveTableShell columns={columns} data={workOrders} paginated pageSize={20} />
 * ```
 */
export function ResponsiveTableShell<T>(props: ResponsiveTableShellProps<T>) {
  const {
    cardTitleColumnId,
    columns,
    data,
    loading = false,
    emptyMessage = 'No data available',
    onRowClick,
    rowKey = (row) => String((row as { id?: string | number }).id ?? ''),
    paginated = false,
    pageSize = 10,
    className,
  } = props;

  return (
    <>
      {/* lg and up: the full data table. */}
      <div className="hidden lg:block">
        <DataTable {...props} />
      </div>
      {/* Below lg: the disclosure card-grid. */}
      <div className="lg:hidden">
        <ResponsiveCardGrid
          columns={columns}
          data={data}
          loading={loading}
          emptyMessage={emptyMessage}
          onRowClick={onRowClick}
          rowKey={rowKey}
          paginated={paginated}
          pageSize={pageSize}
          className={className}
          cardTitleColumnId={cardTitleColumnId}
        />
      </div>
    </>
  );
}

function ResponsiveCardGrid<T>({
  columns,
  data,
  loading,
  emptyMessage,
  onRowClick,
  rowKey,
  paginated,
  pageSize,
  className,
  cardTitleColumnId,
}: {
  columns: DataTableColumn<T>[];
  data: T[];
  loading: boolean;
  emptyMessage: string;
  onRowClick?: (row: T, index: number) => void;
  rowKey: (row: T) => string;
  paginated: boolean;
  pageSize: number;
  className?: string;
  cardTitleColumnId?: string;
}) {
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);
  const [page, setPage] = React.useState(0);

  const titleColumn =
    columns.find((c) => c.id === cardTitleColumnId) ?? columns[0];
  const detailColumns = columns.filter((c) => c.id !== titleColumn?.id);
  const sortableColumns = columns.filter((c) => c.sortable);

  const cycleSort = (columnId: string) => {
    if (sortColumn !== columnId) {
      setSortColumn(columnId);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') setSortDirection('desc');
    else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn || !sortDirection) return data;
    const column = columns.find((c) => c.id === sortColumn);
    if (!column) return data;
    return [...data].sort((a, b) => {
      const av = getAccessorValue(a, column.accessor);
      const bv = getAccessorValue(b, column.accessor);
      if (av === bv) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const a1 = av as number | string;
      const b1 = bv as number | string;
      const cmp = a1 < b1 ? -1 : a1 > b1 ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, sortColumn, sortDirection, columns]);

  const pageCount = paginated ? Math.ceil(sortedData.length / pageSize) : 1;

  React.useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
  }, [pageCount, page]);

  const pageData = React.useMemo(() => {
    if (!paginated) return sortedData;
    const safePage = Math.min(page, Math.max(0, pageCount - 1));
    const start = safePage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize, paginated, pageCount]);

  const renderCell = (col: DataTableColumn<T>, row: T) => {
    const value = getAccessorValue(row, col.accessor);
    return col.cell ? col.cell(value, row) : value == null ? '—' : String(value);
  };

  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: Math.min(pageSize, 4) }).map((_, i) => (
          <Card key={`card-skeleton-${i}`}>
            <CardHeader>
              <Skeleton className="h-5 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (pageData.length === 0) {
    return (
      <div
        className={cn(
          'flex h-24 items-center justify-center rounded-md border text-center text-muted-foreground',
          className,
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {sortableColumns.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Sort by</span>
          {sortableColumns.map((col) => {
            const active = sortColumn === col.id;
            return (
              <Button
                key={col.id}
                type="button"
                variant={active ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => cycleSort(col.id)}
                aria-pressed={active}
                className="inline-flex items-center gap-1"
              >
                {typeof col.header === 'string' ? col.header : col.id}
                {active && sortDirection === 'asc' && <ChevronUp className="h-3.5 w-3.5" />}
                {active && sortDirection === 'desc' && <ChevronDown className="h-3.5 w-3.5" />}
                {!active && <ChevronsUpDown className="h-3.5 w-3.5 opacity-30" />}
              </Button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {pageData.map((row, index) => {
          const id = rowKey(row);
          return (
            <Collapsible key={id} className="rounded-md border bg-card">
              <Card className="border-0 shadow-none">
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 py-3">
                  <CardTitle
                    className={cn(
                      'text-sm font-semibold',
                      onRowClick &&
                        'cursor-pointer rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    )}
                    onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                    // Mirror the desktop row's keyboard contract on the
                    // responsive card so the phone / split-screen layout is
                    // equally operable — only when a click action exists.
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    aria-roledescription={
                      onRowClick ? 'Press Enter or Space to open.' : undefined
                    }
                    onKeyDown={
                      onRowClick
                        ? (e) => activateOnEnterOrSpace(e, () => onRowClick(row, index))
                        : undefined
                    }
                  >
                    {titleColumn ? renderCell(titleColumn, row) : id}
                  </CardTitle>
                  {detailColumns.length > 0 && (
                    <CollapsibleTrigger
                      className="group inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                      aria-label="Toggle row details"
                    >
                      <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                    </CollapsibleTrigger>
                  )}
                </CardHeader>
                {detailColumns.length > 0 && (
                  <CollapsibleContent>
                    <CardContent className="space-y-1.5 py-0 pb-3 text-sm">
                      {detailColumns.map((col) => (
                        <div
                          key={col.id}
                          className="flex items-start justify-between gap-3 border-t border-border/40 pt-1.5 first:border-t-0 first:pt-0"
                        >
                          <span className="text-xs text-muted-foreground">
                            {typeof col.header === 'string' ? col.header : col.id}
                          </span>
                          <span className="text-right">{renderCell(col, row)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </CollapsibleContent>
                )}
              </Card>
            </Collapsible>
          );
        })}
      </div>

      {paginated && sortedData.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing{' '}
            <span className="font-medium">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedData.length)}
            </span>{' '}
            of <span className="font-medium">{sortedData.length}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
              disabled={page >= pageCount - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
